import { useTranslate } from "@similarweb/i18n-provider";
import { OpenFolder } from "@similarweb/icons";
import { ItemIcon, Tooltip, Type } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { ONE } from "app/pages/activation/components/Header/constants";
import { TOOLTIP_DELAY } from "app/pages/activation/components/HomeWithData/constants";
import { projectsDataAutomation } from "app/pages/activation/components/HomeWithData/dataAutomationKeys";
import { COMPETITORS_TEXT } from "app/pages/activation/components/HomeWithData/Projects/texts";
import { VIEW_PROJECT } from "app/pages/activation/components/HomeWithData/texts";
import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import { prepareTooltip } from "app/pages/activation/helpers/home/text";
import type { ActivationMetadataItem } from "app/pages/activation/types/api";

import { ItemIcons } from "./ItemIcons";
import * as SC from "./styles";
import { useItem } from "./useItem";

type ItemSchema = {
    activation: ActivationMetadataItem;
    onClick?: () => void;
    domainIcon: string;
    isIconsLoading: boolean;
};

export const Item = ({ activation, onClick, domainIcon, isIconsLoading }: ItemSchema) => {
    const { activeActivationId } = useHomeContext();
    const { competitorsDomains } = activation;
    const { translate } = useTranslate();

    const { countryObject, onViewClick, onEmailClick, onRouteSubscriptions, onSettingsClick } = useItem({
        activation,
    });

    const isActive = activeActivationId === activation.id;
    const firstCompetitor = competitorsDomains?.[0];

    return (
        <SC.ItemContainer
            $active={isActive}
            onClick={onClick}
            data-active={isActive}
            data-automation={projectsDataAutomation.projectItem(activation.id)}
        >
            {isIconsLoading || !domainIcon ? (
                <SC.IconContainer>
                    <OpenFolder size="xs" />
                </SC.IconContainer>
            ) : (
                <ItemIcon
                    className="webIcon"
                    iconSrc={domainIcon}
                    iconType={Type.Website}
                />
            )}

            <Stack>
                <SC.NameText
                    fontVariant="chip1TopRow"
                    data-automation={projectsDataAutomation.itemName}
                >
                    {activation.name}
                </SC.NameText>

                <SC.InfoContainer>
                    {competitorsDomains.length > ONE ? (
                        <Tooltip
                            enabled={competitorsDomains.length > ONE}
                            content={<SC.TooltipBody>{prepareTooltip(competitorsDomains)}</SC.TooltipBody>}
                            placement="bottom"
                            delay={TOOLTIP_DELAY}
                        >
                            <SC.CompetitorsText
                                fontVariant="dataTable2"
                                data-automation={projectsDataAutomation.itemCompetitors}
                            >
                                {translate(COMPETITORS_TEXT, { count: competitorsDomains.length })}
                            </SC.CompetitorsText>
                        </Tooltip>
                    ) : (
                        <SC.CompetitorsText
                            fontVariant="dataTable2"
                            data-automation={projectsDataAutomation.itemCompetitors}
                        >
                            vs {firstCompetitor}
                        </SC.CompetitorsText>
                    )}

                    {isActive ? (
                        <ItemIcons
                            activation={activation}
                            translate={translate}
                            countryObject={countryObject}
                            onEmailClick={onEmailClick}
                            onRouteSubscriptions={onRouteSubscriptions}
                            onSettingsClick={onSettingsClick}
                        />
                    ) : null}
                </SC.InfoContainer>
            </Stack>

            {isActive ? (
                <SC.ViewButton
                    type="outlined"
                    onClick={onViewClick}
                    dataAutomation={projectsDataAutomation.viewProjectButton}
                >
                    {translate(VIEW_PROJECT)}
                </SC.ViewButton>
            ) : null}
        </SC.ItemContainer>
    );
};
